// A user's "extra roles" (developer/tester/durian/erdnuss - see EXTRA_ROLES below, and the matching
// KNOWN_EXTRA_ROLES whitelist in functions/express/utils/user-roles.js) are stored as a single array custom claim,
// extra_roles, that a person can have any combination of on top of their regular employee/admin/
// super/client access level.
//
// "durian" replaces the older standalone durian_member boolean claim. A user who was already a
// Durian member before this migration shipped, and whose extra_roles claim has never been
// written since (i.e. nobody has saved a change to their Employees row yet, so their token still
// only carries the old durian_member: true claim), still needs to count as having the "durian"
// role - otherwise they'd silently lose Durian access the moment this shipped, just because
// nobody happened to touch their row. functions/express/routes/users/update-user.js separately
// folds durian_member into extra_roles for real (and keeps the two in sync) the next time
// anything at all updates that user, so this fallback only ever matters for users that hasn't
// happened for yet.
export function getExtraRoles(userClaims) {
  const roles = userClaims?.["extra_roles"] ?? [];
  if ((userClaims?.["durian_member"] ?? false) && !roles.includes("durian")) {
    return [...roles, "durian"];
  }
  return roles;
}

export function hasExtraRole(userClaims, role) {
  return getExtraRoles(userClaims).includes(role);
}

// Every extra role that can be assigned on the Employees page, with the label and badge colour used
// wherever roles are shown or picked (Employees, Profile, and a post's audience). The values must
// match KNOWN_EXTRA_ROLES in functions/express/utils/user-roles.js, or a role chosen here would be
// silently dropped when saved.
export const EXTRA_ROLES = [
  { value: "developer", label: "Developer" },
  { value: "tester", label: "Tester" },
  { value: "durian", label: "Durian" },
  { value: "erdnuss", label: "Erdnuss" },
];

export const EXTRA_ROLE_COLORS = {
  developer: "cyan",
  tester: "orange",
  durian: "grape",
  erdnuss: "teal",
};

// Which roles a post is restricted to - an empty list means everyone. New posts store this in
// `audience_roles`; posts written before roles could be picked only have the old `audience` string,
// where "durian" meant Durian members only and anything else (or nothing) meant everyone, so that
// still has to be understood or every existing Durian-only post would become public.
export function getPostAudienceRoles(post) {
  if (Array.isArray(post?.audience_roles) && post.audience_roles.length > 0) {
    return post.audience_roles;
  }
  return post?.audience === "durian" ? ["durian"] : [];
}

// A post for specific roles is visible to someone holding at least one of them (not all of them).
export function isPostForViewer(post, userClaims) {
  const audienceRoles = getPostAudienceRoles(post);
  if (audienceRoles.length === 0) return true;
  const viewerRoles = getExtraRoles(userClaims);
  return audienceRoles.some((role) => viewerRoles.includes(role));
}
